import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { getCommodityFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";

/**
 * The initial commodity row is created on mount (via `ensureInitialRows`), which can happen
 * before the location's `default_currency` has loaded. Since that currency is only applied at
 * commodity-creation time, the first commodity would otherwise keep an empty currency forever.
 *
 * This backfills `default_currency` onto any commodity whose currency is still empty, once the
 * default currency becomes available - mirroring the old request form, where an empty commodity
 * currency falls back to the location default.
 */
export function useApplyDefaultCommodityCurrency(defaultCurrency: string) {
  const { getValues, setValue } = useFormContext();
  const hasAppliedRef = useRef(false);

  useEffect(() => {
    if (hasAppliedRef.current || !defaultCurrency) return;

    const rows = (getValues("packing_list") ?? []) as PackingListRow[];
    rows.forEach((row, packingListIndex) => {
      row.good_customs_information?.forEach((commodity, commodityIndex) => {
        if (commodity.currency_code) return;
        setValue(getCommodityFieldPath(packingListIndex, commodityIndex, "currency_code"), defaultCurrency, {
          shouldDirty: true,
        });
      });
    });

    hasAppliedRef.current = true;
  }, [defaultCurrency, getValues, setValue]);
}
